<script>
import { periodicTasksMethods } from '@state/helpers'
import { required, maxLength } from 'vuelidate/lib/validators'
import ClockedScheduleSelector from '@components/scheduled-tasks/clocked-schedule/select'
import IntervalScheduleSelector from '@components/scheduled-tasks/interval-schedule/select'
import CrontabScheduleSelector from '@components/scheduled-tasks/crontab-schedule/select'

export default {
  name: 'PeriodicTaskForm',
  components: {
    ClockedScheduleSelector,
    CrontabScheduleSelector,
    IntervalScheduleSelector,
  },
  props: {
    instanceJson: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    oncomplete: {
      type: Function,
      required: false,
      default: () => ({}),
    },
    oncancel: {
      type: Function,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      modalShow: true,
      loading: true,
      submitting: false,
      action: 'create',
      timingSelected: null,
      timingOptions: [
        {
          value: 'clocked',
          text: 'Fecha programada',
        },
        {
          value: 'interval',
          text: 'Intervalo',
        },
        {
          value: 'crontab',
          text: 'Crontab',
        },
      ],
      periodicTasksOptions: [],
      form: {
        url: null,
        name: '',
        task: '',
        interval: null,
        crontab: '',
        solar: null,
        clocked: null,
        args: '[]',
        kwargs: '{}',
        queue: null,
        exchange: null,
        routing_key: null,
        headers: '{}',
        priority: null,
        expires: null,
        expire_seconds: null,
        one_off: false,
        start_time: null,
        enabled: true,
        description: '',
      },
    }
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(150),
      },
      task: { required },
      interval: {},
      crontab: {},
      solar: {},
      clocked: {},
      args: {},
      kwargs: {},
      queue: {},
      exchange: {},
      routing_key: {},
      headers: {},
      priority: {},
      expires: {},
      expire_seconds: {},
      one_off: {},
      start_time: {},
      enabled: {},
      description: {},
    },
  },
  computed: {},

  mounted: function () {
    let context = this
    if (context.instanceJson.url) {
      context.action = 'update'
      context.form = {
        ...context.instanceJson,
      }
      context.getObject()
    } else {
      context.action = 'create'
      context.loading = false
    }
    context.populatePeriodicTasksOptions()
  },

  methods: {
    ...periodicTasksMethods,

    getObject() {
      let context = this
      if (context.action == 'update') {
        const promise = context.fetchPeriodicTasksDetail(context.form.url)
        return promise
          .then((data) => {
            context.form = { ...data }
            if (data.clocked) {
              context.timingSelected = 'clocked'
            } else if (data.interval) {
              context.timingSelected = 'interval'
            } else if (data.crontab) {
              context.timingSelected = 'crontab'
            }
            context.loading = false
          })
          .catch((error) => {
            context.$axios_error_noty(error, context)
            context.loading = false
          })
      }
    },

    populatePeriodicTasksOptions() {
      let context = this
      const promise = context.fetchPeriodicTasksOptions()
      return promise
        .then((data) => {
          let listOptions = []
          data.forEach((opt) => {
            listOptions.push({
              value: opt[0],
              text: opt[1],
            })
          })
          context.periodicTasksOptions = listOptions
        })
        .catch((error) => {
          context.$axios_error_noty(error, context)
        })
    },

    submit() {
      let context = this
      context.submitting = true
      context.$v.form.$touch()
      if (context.$v.form.$invalid) {
        context.submitting = false
        context.$generate_toast(
          context,
          'error',
          'Por favor llene todos los campos requeridos'
        )
        return
      }
      if (context.timingSelected === 'clocked') {
        if (!context.form.one_off) {
          context.submitting = false
          context.$generate_toast(
            context,
            'error',
            'Debe habilitar el campo "Ejecutar sólo una vez?" para utilizar una Fecha programada'
          )
          return
        }
        context.form.interval = null
        context.form.crontab = null
      } else if (context.timingSelected === 'interval') {
        context.form.clocked = null
        context.form.crontab = null
      } else if (context.timingSelected === 'crontab') {
        context.form.clocked = null
        context.form.interval = null
      }
      if (!context.timingSelected || !context.form[context.timingSelected]) {
        context.submitting = false
        context.$generate_toast(
          context,
          'error',
          'Por favor configure un tipo de programación'
        )
        return
      }
      if (context.form.kwargs){
        context.form.kwargs = JSON.stringify(JSON.parse(context.form.kwargs), null, 2)
      }
      if (context.form.args){
        context.form.args = JSON.stringify(JSON.parse(context.form.args), null, 2)
      }
      const promise = context.createUpdatePeriodicTasks(context.form)
      return promise
        .then((data) => {
          context.$generate_toast(
            context,
            'success',
            `Tarea periódica ${
              context.action == 'create' ? 'creada' : 'actualizada'
            } correctamente`
          )
          setTimeout(() => {
            context.oncomplete()
            context.submitting = false
          }, 2000)
        })
        .catch((error) => {
          context.submitting = false
          context.$axios_error_noty(error, context)
        })
    },
  },
}
</script>

<template>
  <b-modal
    v-model="modalShow"
    :title="
      action === 'create' ? 'Crear Tarea periódica' : 'Editar Tarea periódica'
    "
    :size="action === 'create' ? 'xl' : 'xl'"
    @hidden="oncancel"
    @ok.prevent="submit"
  >
    <b-container class="p-0">
      <b-row v-if="loading">
        <b-col md="12">
          <div class="text-center">
            <b-spinner
              label="Spinning"
              style="color: #195db2; width: 4rem; height: 4rem"
            ></b-spinner>
          </div>
        </b-col>
      </b-row>
      <b-row v-else>
        <b-col md="12">
          <b-form id="form">
            <form-wrapper :validator="$v.form">
              <b-tabs>
                <b-tab title="Información básica">
                  <b-row>
                    <b-col md="6">
                      <b-row>
                        <b-col md="6">
                          <form-group
                            name="name"
                            label="Nombre"
                            attribute="Nombre"
                          >
                            <b-form-input
                              v-model="form.name"
                              slot-scope="{ attrs, listeners }"
                              v-bind="attrs"
                              v-on="listeners"
                            />
                          </form-group>
                        </b-col>
                        <b-col md="6">
                          <b-form-group label="Tarea">
                            <b-form-select
                              v-model="form.task"
                              :options="periodicTasksOptions"
                            ></b-form-select>
                          </b-form-group>
                          <!-- <form-group name="task" label="Tarea">
                            <b-form-input
                              v-model="form.task"
                              slot-scope="{ attrs, listeners }"
                              v-bind="attrs"
                              v-on="listeners"
                            />
                          </form-group> -->
                        </b-col>
                      </b-row>
                      <form-group name="description" label="Descripción">
                        <b-form-textarea
                          v-model="form.description"
                          rows="6"
                          slot-scope="{ attrs, listeners }"
                          v-bind="attrs"
                          v-on="listeners"
                        />
                      </form-group>
                      <b-form-checkbox
                        v-model="form.one_off"
                        @change="$v.form.one_off.$touch()"
                      >
                        Ejecutar sólo una vez?
                      </b-form-checkbox>

                      <b-form-checkbox
                        v-model="form.enabled"
                        @change="$v.form.enabled.$touch()"
                      >
                        Habilitar tarea?
                      </b-form-checkbox>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="Tipo de programación">
                        <b-form-select
                          v-model="timingSelected"
                          :options="timingOptions"
                        ></b-form-select>
                      </b-form-group>
                      <ClockedScheduleSelector
                        v-if="timingSelected === 'clocked'"
                        v-model="form.clocked"
                      />
                      <IntervalScheduleSelector
                        v-if="timingSelected === 'interval'"
                        v-model="form.interval"
                      />
                      <CrontabScheduleSelector
                        v-if="timingSelected === 'crontab'"
                        v-model="form.crontab"
                      />
                    </b-col>
                  </b-row>
                </b-tab>
                <b-tab title="Argumentos de ejecución">
                  <b-row>
                    <b-col md="6">
                      <form-group name="args" label="args">
                        <b-form-textarea
                          v-model="form.args"
                          rows="6"
                          slot-scope="{ attrs, listeners }"
                          v-bind="attrs"
                          v-on="listeners"
                        />
                      </form-group>
                      <form-group name="kwargs" label="kwargs">
                        <b-form-textarea
                          v-model="form.kwargs"
                          rows="6"
                          slot-scope="{ attrs, listeners }"
                          v-bind="attrs"
                          v-on="listeners"
                        />
                      </form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group
                        name="start_time"
                        label="Fecha de inicio de ejecución"
                      >
                        <VueCtkDateTimePicker
                          v-model="form.start_time"
                          id="start_time"
                          format="YYYY-MM-DDTHH:mm:ss.sssZ"
                        />
                        <span class="text-muted"
                          >Fecha desde la que se comenzará a ejecutar la
                          tarea</span
                        >
                      </b-form-group>
                      <b-form-group name="expires" label="Fecha de expiración">
                        <VueCtkDateTimePicker
                          v-model="form.expires"
                          id="expires"
                          format="YYYY-MM-DDTHH:mm:ss.sssZ"
                        />
                        <span class="text-muted"
                          >La tarea no se ejecutará más a partir de esta
                          fecha</span
                        >
                      </b-form-group>
                      <form-group
                        name="expire_seconds"
                        label="Total segundos para expirar"
                        helptext="La tarea no se volverá a ejecutar luego del tiempo
                          indicado"
                      >
                        <b-form-input
                          v-model="form.expire_seconds"
                          slot-scope="{ attrs, listeners }"
                          v-bind="attrs"
                          v-on="listeners"
                        />
                      </form-group>
                    </b-col>
                  </b-row>
                </b-tab>
              </b-tabs>
              <div>
                <form-summary />
              </div>
            </form-wrapper>
          </b-form>
        </b-col>
      </b-row>
    </b-container>

    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button  variant="outline-primary shadow rounded" @click="cancel">
        Cancelar
      </b-button>

      <b-button
        variant="primary shadow rounded"
        :class="{ disabled: submitting }"
        @click="ok"
      >
        <b-spinner v-if="submitting" label="Spinning" small></b-spinner>
        <span v-if="submitting"> Guardando </span>
        <span v-else>
          <i class="fe-save"></i>
          Guardar
        </span>
      </b-button>
    </template>
  </b-modal>
</template>
<style lang="scss">
</style>